import React, { useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import _ from 'underscore';
import moment from 'moment';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';

import { Table, TableBody, TableHead, TableRow, TableHeading, TableData } from 'components/CMSRankList/styles';
import * as actions from './actions';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import { Header, HeadingContainer } from 'components/PageTitleHeader/styles';
import TabSelector from 'components/TabSelector/TabSelector';
import {
  InputAndSearchSection,
  SelectTestLabel,
  DateLabel,
  InputSection,
  FeedbackContainer,
  FeedbackCard,
  LabelContainer,
  LabelHeading,
  QuestionContainer,
  HorizontalLine,
  AnswerLabel,
  FeedbackCheckbox,
  FeedbackInputCheckbox,
  CanvasContainer,
} from './styles';
import Dropdown from 'components/CustomizableDropdown/CustomizableDropdown';
import { userRoles } from 'constants/users';
import { useActions } from 'hooks/useActions';
import services from 'api/services';

const { FeedbackService } = services;
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default React.memo(() => {
  const { userRole } = useSelector((state) => state.auth);

  const [feedbacksArray, setFeedbacksArray] = useState([]);
  const [dataSet, setDataSet] = useState([]);

  const [testsList, setTestsList] = useState([]);

  const minimumDate = moment(new Date()).subtract(1, 'month').format('YYYY-MM-DD');
  const maximumDate = moment(new Date()).format('YYYY-MM-DD');

  const [minDate, setMinDate] = useState(minimumDate);
  const [maxDate, setMaxDate] = useState(maximumDate);
  const [test, setTest] = useState('');
  const [summaryData, setSummaryData] = useState([]);

  const { getPublishedTests, getCandidateFeedbacks, updateCandidateFeedback } = useActions(actions);

  const getSummary = async (minDate, maxDate) => {
    try {
      const response = await FeedbackService.getFeedbackSummary({ minDate, maxDate });
      setSummaryData(response);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : e?.message || 'Could not get summary');
    }
  };

  useEffect(() => {
    getPublishedTests();
  }, [getPublishedTests]);

  let { tests, feedbacks, visualization, statistic, loadingFeedback } = useSelector(
    (state) => state.CandidateFeedbackPageReducer
  );

  useEffect(() => {
    const mappedTestList = tests.map((test) => {
      test = { ...test };
      test.label = test.name;
      test.value = test._id;
      delete test.name;
      return test;
    });
    setTestsList(mappedTestList);
    if (mappedTestList.length) {
      setTest(mappedTestList[0]._id);
      getCandidateFeedbacks({ minDate: minimumDate, maxDate: maximumDate, test: mappedTestList[0]._id });
    }
  }, [tests, getCandidateFeedbacks, minimumDate, maximumDate]);

  useEffect(() => {
    if (!statistic._id) return;
    let index = _.findIndex(feedbacksArray, (i) => i._id === statistic._id);
    let tempFeedbacks = JSON.parse(JSON.stringify(feedbacksArray));
    tempFeedbacks[index].showFeedback = statistic.showFeedback;
    setFeedbacksArray(tempFeedbacks);
  }, [statistic, feedbacksArray]);

  useEffect(() => {
    setFeedbacksArray(feedbacks);
  }, [feedbacks]);

  useEffect(() => {
    setDataSet(visualization);
  }, [visualization]);

  const renderVisualization = () => {
    if (!dataSet) return;
    const charts = dataSet.map((value, index) => {
      let data = {
        labels: _.pluck(value.review, 'value'),
        datasets: [
          {
            backgroundColor: ['#3e95cd', '#8e5ea2', '#3cba9f', '#e8c3b9'],
            data: _.pluck(value.review, 'count'),
          },
        ],
      };
      let options = {
        plugins: {
          title: {
            font: { size: 18 },
            display: true,
            text: value.question,
            position: 'bottom',
          },
          legend: { display: false },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
          datalabels: false,
        },
      };
      return <Bar key={index} data={data} options={options}></Bar>;
    });
    return <CanvasContainer>{charts}</CanvasContainer>;
  };

  const renderFeedbackCard = () => {
    if (loadingFeedback) return <FeedbackContainer>Loading</FeedbackContainer>;
    if (!(feedbacksArray && feedbacksArray.length)) return <FeedbackContainer>No data found</FeedbackContainer>;
    return (
      <FeedbackContainer>
        {feedbacksArray.map((feedback, index) => {
          return (
            <FeedbackCard key={index}>
              <LabelContainer>
                <LabelHeading>Name:&nbsp;</LabelHeading>
                <Label>{feedback.candidateName}</Label>
              </LabelContainer>
              <LabelContainer>
                <LabelHeading>Company:&nbsp;</LabelHeading>
                <Label>{feedback.company}</Label>
              </LabelContainer>
              <LabelContainer>
                <LabelHeading>Test:&nbsp;</LabelHeading>
                <Label>{feedback.testName}</Label>
              </LabelContainer>
              <LabelContainer>
                <LabelHeading>Date:&nbsp;</LabelHeading>
                <Label>{moment(feedback.createdAt).format('DD/MM/YYYY')}</Label>
              </LabelContainer>
              <HorizontalLine />
              {feedback.feedback.map((f, index) => {
                if (index === 3) return <Fragment key={index}></Fragment>;
                return (
                  <QuestionContainer key={index}>
                    <LabelHeading>
                      Q{index + 1}. {f.question}&nbsp;
                    </LabelHeading>
                    <AnswerLabel>{f.review || 'No answer given'}</AnswerLabel>
                  </QuestionContainer>
                );
              })}
              {feedback?.feedback[3]?.comment && (
                <LabelContainer padding="1rem 0 0 0">
                  <LabelHeading>Comment:&nbsp;</LabelHeading>
                  <Label>{feedback.feedback[3].comment}</Label>
                </LabelContainer>
              )}
              {userRole === userRoles.SUPER_ADMIN && (
                <>
                  <HorizontalLine />
                  <LabelContainer>
                    <LabelHeading>Show feedback to customer:&nbsp;</LabelHeading>
                    <FeedbackCheckbox>
                      <FeedbackInputCheckbox
                        type="checkbox"
                        checked={feedback.showFeedback}
                        onChange={() =>
                          updateCandidateFeedback({ _id: feedback._id, showFeedback: !feedback.showFeedback })
                        }
                      />
                    </FeedbackCheckbox>
                  </LabelContainer>
                </>
              )}
            </FeedbackCard>
          );
        })}
      </FeedbackContainer>
    );
  };

  const renderBottomSection = () => {
    let items = [
      { label: 'Feedback', component: renderFeedbackCard() },
      { label: 'Visualization', component: renderVisualization() },
    ];
    if (userRole === userRoles.SUPER_ADMIN) {
      items.push({ label: 'Summary', component: renderSummaryTable(summaryData) });
    }
    return <TabSelector items={items}></TabSelector>;
  };

  const renderTopSection = () => {
    return (
      <InputAndSearchSection showSummaryButton={userRole === userRoles.SUPER_ADMIN}>
        <InputSection>
          <DateLabel>Start Date:</DateLabel>
          <Input type="date" value={minDate} onChange={(e) => setMinDate(e.target.value)} />
        </InputSection>
        <InputSection>
          <DateLabel>End Date:</DateLabel>
          <Input type="date" value={maxDate} onChange={(e) => setMaxDate(e.target.value)} />
        </InputSection>
        <InputSection>
          <SelectTestLabel>Select Test:</SelectTestLabel>
          <Dropdown
            options={testsList}
            onChange={(returnedValue) => {
              const test = _.findWhere(testsList, { value: returnedValue.value });
              setTest(test._id);
            }}
          />
        </InputSection>
        <InputSection>
          <Button
            primaryBlue
            primaryGreenBorder
            small
            removeMargin
            onClick={() => {
              getCandidateFeedbacks({ minDate, maxDate, test });
            }}
          >
            Search
          </Button>
          {userRole === userRoles.SUPER_ADMIN && (
            <Button
              primaryBlue
              primaryGreenBorder
              small
              removeMargin
              onClick={() => {
                getSummary(minDate, maxDate);
              }}
            >
              Get Summary
            </Button>
          )}
        </InputSection>
      </InputAndSearchSection>
    );
  };

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100%">
      <Container column>
        <HeadingContainer>
          <Header>Candidate Feedback</Header>
        </HeadingContainer>
        {renderTopSection()}
        {renderBottomSection()}
      </Container>
    </Wrapper>
  );
});

function renderSummaryTable(summaryData) {
  return (
    <FeedbackContainer>
      <Table>
        <TableHead>
          <TableRow isHeading>
            <TableHeading>Test Name</TableHeading>
            <TableHeading>Poor</TableHeading>
            <TableHeading>Fair</TableHeading>
            <TableHeading>Good</TableHeading>
            <TableHeading>Very Good</TableHeading>
            <TableHeading>Total</TableHeading>
            <TableHeading>Score</TableHeading>
          </TableRow>
        </TableHead>
        <TableBody>
          {summaryData.map((item, index) => {
            return (
              <TableRow key={index}>
                <TableData>{item.testName}</TableData>
                <TableData>{item.Poor}</TableData>
                <TableData>{item.Fair}</TableData>
                <TableData>{item.Good}</TableData>
                <TableData>{item['Very Good']}</TableData>
                <TableData>{item.total}</TableData>
                <TableData>{item.score}</TableData>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </FeedbackContainer>
  );
}
